import { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "@chakra-ui/react";
import { connectSocket, disconnectSocket } from "../components/Chat/socketClient";

const MAX_MESSAGES = 50;
const MAX_THREADS = 50;

function safeParse(json) {
  try { return JSON.parse(json); } catch { return null; }
}
const sessionKey = (clinicaId, peerKey) => `chat:${clinicaId}:${peerKey}`;
const labelsKey = (clinicaId) => `chat:labels:${clinicaId}`;
const recentsKey = (clinicaId) => `chat:recents:${clinicaId}`;

function loadJson(key, fallback) {
  const raw = localStorage.getItem(key);
  return (raw ? safeParse(raw) : null) ?? fallback;
}
function saveJson(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

/**
 * Gerencia o ciclo de vida do socket do chat interno.
 *
 * @param {{ isOpen: boolean, clinicaId: string, token: string }} params
 * @returns {{
 *   socketStatus: string,
 *   lastSocketError: string,
 *   me: object|null,
 *   onlineUsers: object[],
 *   messagesByPeer: object,
 *   threads: object[],
 *   loadHistory: (peerKey: string) => void,
 *   sendMessage: (opts: { toUserKey: string, text: string, onSuccess?: () => void, onError?: (msg: string) => void }) => void,
 *   rejoinClinic: () => void,
 *   addThread: (peerKey: string, label: string) => void,
 * }}
 */
export function useChatSocket({ isOpen, clinicaId, token }) {
  const toast = useToast();

  const [socketStatus, setSocketStatus] = useState("desconectado");
  const [lastSocketError, setLastSocketError] = useState("");
  const [me, setMe] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [messagesByPeer, setMessagesByPeer] = useState({});
  const [threads, setThreads] = useState([]);

  const meKeyRef = useRef(null);
  const messagesByPeerRef = useRef({});

  useEffect(() => {
    messagesByPeerRef.current = messagesByPeer;
  }, [messagesByPeer]);

  const getSafeSocket = useCallback(() => {
    if (!token) return null;
    try { return connectSocket({ token }); } catch { return null; }
  }, [token]);

  // Conexão + handlers de eventos
  useEffect(() => {
    if (!isOpen) return;

    if (!token) {
      setSocketStatus("erro");
      setLastSocketError("Token ausente");
      toast({ title: "Chat indisponível", description: "Você precisa estar logado.", status: "warning", duration: 4000, isClosable: true });
      return;
    }

    let socket;
    try {
      setSocketStatus("conectando");
      setLastSocketError("");
      socket = connectSocket({ token });
    } catch (e) {
      setSocketStatus("erro");
      setLastSocketError(e?.message || "Não foi possível conectar");
      toast({ title: "Erro ao conectar no chat", description: e?.message, status: "error", duration: 5000, isClosable: true });
      return;
    }

    const onConnect = () => { setSocketStatus("conectado"); setLastSocketError(""); };
    const onDisconnect = () => setSocketStatus("desconectado");
    const onConnectError = (err) => {
      setSocketStatus("erro");
      setLastSocketError(err?.message || "Falha ao conectar");
    };

    const onMe = (payload) => {
      if (!payload?.userKey) return;
      meKeyRef.current = payload.userKey;
      setMe(payload);
    };

    const onPresence = (payload) => {
      if (payload?.clinicaId && String(payload.clinicaId) !== String(clinicaId || "")) return;
      const users = Array.isArray(payload?.users) ? payload.users : [];
      setOnlineUsers(users);
      if (!clinicaId) return;
      const map = loadJson(labelsKey(clinicaId), {});
      users.forEach((u) => { if (u?.userKey && u?.label) map[u.userKey] = u.label; });
      saveJson(labelsKey(clinicaId), map);
    };

    const onMessage = (msg) => {
      if (!msg?.clinicaId || String(msg.clinicaId) !== String(clinicaId || "")) return;
      const myKey = meKeyRef.current;
      const peerKey = msg.fromUserKey === myKey ? msg.toUserKey : msg.fromUserKey;
      if (!peerKey) return;

      setMessagesByPeer((prev) => {
        const arr = Array.isArray(prev[peerKey]) ? [...prev[peerKey]] : [];
        const sliced = [...arr, msg].slice(-MAX_MESSAGES);
        if (clinicaId) saveJson(sessionKey(clinicaId, peerKey), sliced);
        return { ...prev, [peerKey]: sliced };
      });

      if (!clinicaId) return;
      const labels = loadJson(labelsKey(clinicaId), {});
      if (msg.fromUserKey && msg.fromLabel && msg.fromUserKey !== meKeyRef.current) {
        labels[msg.fromUserKey] = msg.fromLabel;
        saveJson(labelsKey(clinicaId), labels);
      }
      const label = labels?.[peerKey] || msg.fromLabel || peerKey;
      setThreads((prev) => {
        const next = [
          { peerUserKey: peerKey, label, lastTs: msg.ts, lastText: msg.text || "" },
          ...(Array.isArray(prev) ? prev : []).filter((t) => t?.peerUserKey !== peerKey),
        ].slice(0, MAX_THREADS);
        saveJson(recentsKey(clinicaId), next);
        return next;
      });
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("connect_error", onConnectError);
    socket.on("session:me", onMe);
    socket.on("presence:update", onPresence);
    socket.on("chat:message", onMessage);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("connect_error", onConnectError);
      socket.off("session:me", onMe);
      socket.off("presence:update", onPresence);
      socket.off("chat:message", onMessage);
    };
  }, [isOpen, clinicaId, token, toast]);

  // Join da clínica ao abrir
  useEffect(() => {
    if (!isOpen || !clinicaId || !token) return;
    const socket = getSafeSocket();
    if (!socket) return;

    socket.emit("clinic:join", { clinicaId }, (resp) => {
      if (!resp?.ok) {
        const msg = resp?.error || "Não foi possível entrar na clínica";
        setLastSocketError(msg);
        toast({ title: "Chat indisponível nesta clínica", description: msg, status: "error", duration: 5000, isClosable: true });
        return;
      }
      setLastSocketError("");
      setOnlineUsers(Array.isArray(resp.users) ? resp.users : []);
      setThreads(loadJson(recentsKey(clinicaId), []));

      socket.emit("chat:threads", {}, (threadsResp) => {
        if (!threadsResp?.ok) return;
        const labels = loadJson(labelsKey(clinicaId), {});
        const list = Array.isArray(threadsResp.threads) ? threadsResp.threads : [];
        list.forEach((t) => {
          if (t?.lastFromUserKey && t?.lastFromLabel && t.lastFromUserKey !== meKeyRef.current) {
            labels[t.lastFromUserKey] = t.lastFromLabel;
          }
        });
        saveJson(labelsKey(clinicaId), labels);
        const mapped = list.map((t) => ({
          peerUserKey: t.peerUserKey,
          label: labels?.[t.peerUserKey] || t.peerUserKey,
          lastTs: t.lastTs,
          lastText: t.lastText || "",
        }));
        setThreads(mapped);
        saveJson(recentsKey(clinicaId), mapped);
      });
    });
  }, [isOpen, clinicaId, token, getSafeSocket, toast]);

  // Desconecta e limpa estado ao fechar
  useEffect(() => {
    if (isOpen) return;
    disconnectSocket();
    setSocketStatus("desconectado");
    setLastSocketError("");
    setOnlineUsers([]);
    setThreads([]);
    setMessagesByPeer({});
    messagesByPeerRef.current = {};
    meKeyRef.current = null;
    setMe(null);
  }, [isOpen]);

  const loadHistory = useCallback((peerKey) => {
    if (!peerKey || !clinicaId) return;
    const existing = messagesByPeerRef.current[peerKey];
    if (Array.isArray(existing) && existing.length > 0) return;

    const stored = loadJson(sessionKey(clinicaId, peerKey), null);
    if (Array.isArray(stored) && stored.length > 0) {
      setMessagesByPeer((prev) => ({ ...prev, [peerKey]: stored.slice(-MAX_MESSAGES) }));
      return;
    }

    const socket = getSafeSocket();
    if (!socket) return;
    socket.emit("chat:history", { peerUserKey: peerKey }, (resp) => {
      if (!resp?.ok) return;
      const msgs = (Array.isArray(resp.messages) ? resp.messages : []).slice(-MAX_MESSAGES);
      setMessagesByPeer((prev) => ({ ...prev, [peerKey]: msgs }));
      saveJson(sessionKey(clinicaId, peerKey), msgs);
    });
  }, [clinicaId, getSafeSocket]);

  const sendMessage = useCallback(({ toUserKey, text, onSuccess, onError }) => {
    const socket = getSafeSocket();
    if (!socket) { onError?.("Socket não conectado"); return; }
    socket.emit("chat:send", { toUserKey, text }, (resp) => {
      if (!resp?.ok) { onError?.(resp?.error || "Erro desconhecido"); return; }
      onSuccess?.();
    });
  }, [getSafeSocket]);

  const rejoinClinic = useCallback(() => {
    const socket = getSafeSocket();
    if (!socket || !clinicaId) return;
    socket.emit("clinic:join", { clinicaId }, () => {});
  }, [getSafeSocket, clinicaId]);

  const addThread = useCallback((peerKey, label) => {
    if (!clinicaId) return;
    setThreads((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      if (arr.some((t) => t?.peerUserKey === peerKey)) return arr;
      const next = [
        { peerUserKey: peerKey, label, lastTs: new Date().toISOString(), lastText: "" },
        ...arr,
      ].slice(0, MAX_THREADS);
      saveJson(recentsKey(clinicaId), next);
      return next;
    });
  }, [clinicaId]);

  return {
    socketStatus,
    lastSocketError,
    me,
    onlineUsers,
    messagesByPeer,
    threads,
    loadHistory,
    sendMessage,
    rejoinClinic,
    addThread,
  };
}
